import { AnimatePresence, motion } from 'motion/react';
import { Tooltip as TooltipPrimitive } from 'radix-ui';
import { type MouseEvent, type ReactElement, useState } from 'react';

import { HotkeyLabel } from '@/components/blocks/Hotkey';
import { useShortcutKeys, type ShortcutId } from '@/features/shortcuts';
import { afterFrames } from '@/lib/animate';
import type { KeyName } from '@/lib/key';
import { classList } from '@/utils/cn';

import { XStack } from './layout/layout';

export interface TooltipProps {
  label: string;
  hotkey?: readonly KeyName[] | ShortcutId;
  description?: string;
  children: ReactElement;
  showOnUpdate?: boolean;
  desktop?: boolean;
  side?: TooltipPrimitive.TooltipContentProps['side'];
  sideOffset?: number;
  delayDuration?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  contentClassName?: string;
  disableHoverableContent?: boolean;
  disabled?: boolean;
}

export const TooltipProvider = TooltipPrimitive.TooltipProvider;

export const Tooltip = ({
  label,
  hotkey,
  description,
  children,
  showOnUpdate = false,
  desktop = false,
  side,
  sideOffset = 4,
  delayDuration = 500,
  open: controlledOpen,
  onOpenChange,
  className,
  contentClassName,
  disableHoverableContent = false,
  disabled = false,
}: TooltipProps) => {
  const [open, setOpen] = useState(false);
  if (disabled && open) setOpen(false);

  const isOpen = (controlledOpen ?? open) && !disabled;
  const shortcutId = typeof hotkey === 'string' ? hotkey : undefined;
  const shortcutKeys = useShortcutKeys(shortcutId);
  const resolvedKeys: readonly KeyName[] | undefined =
    typeof hotkey === 'string' ? shortcutKeys : hotkey;

  if (!label && !resolvedKeys?.length && !description) {
    return children;
  }

  const handleOnClick = (event: MouseEvent) => {
    if (event.defaultPrevented) return;

    afterFrames(5, () => {
      if (showOnUpdate) setOpen(true);
    });
  };

  const hasKeys = resolvedKeys && resolvedKeys.length > 0;

  const content = (
    <>
      {hasKeys ? (
        <XStack className="items-center gap-2">
          <span>{label}</span>
          <XStack className="items-center gap-0.5">
            {resolvedKeys.map((key, index) => (
              <HotkeyLabel key={`${key}-${index}`} keyName={key} size="xs" />
            ))}
          </XStack>
        </XStack>
      ) : (
        <span>{label}</span>
      )}
      {description && (
        <span className="text-foreground-muted text-[11px] leading-tight">
          {description}
        </span>
      )}
    </>
  );

  return (
    <TooltipPrimitive.Root
      delayDuration={delayDuration}
      disableHoverableContent={disableHoverableContent}
      open={isOpen}
      onOpenChange={(next) => {
        const nextOpen = next && !disabled;
        setOpen(nextOpen);
        onOpenChange?.(nextOpen);
      }}
    >
      <TooltipPrimitive.Trigger
        onClick={handleOnClick}
        onFocus={(event) => {
          if (!event.currentTarget.matches(':focus-visible')) {
            event.preventDefault();
          }
        }}
        asChild
      >
        {children}
      </TooltipPrimitive.Trigger>
      <AnimatePresence mode="wait">
        {isOpen && (
          <TooltipPrimitive.Portal forceMount>
            <TooltipPrimitive.Content
              side={side}
              sideOffset={sideOffset}
              className={classList('z-50', contentClassName)}
            >
              <motion.div
                initial={{ opacity: 0, y: 2, scale: 0.85 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{
                  opacity: 0,
                  y: 2,
                  transition: {
                    type: 'keyframes',
                    ease: 'easeIn',
                    duration: 0.1,
                  },
                }}
                transition={{
                  type: 'keyframes',
                  duration: 0.1,
                }}
                style={{
                  transformOrigin:
                    'var(--radix-tooltip-content-transform-origin)',
                }}
                className={classList(
                  'px-2 py-1.5',
                  'rounded-lg smoothing-default',
                  'border border-border-strong',
                  'text-foreground-subtle text-xs',
                  'shadow-sm',
                  'bg-surface',
                  description
                    ? 'flex flex-col gap-1'
                    : 'flex flex-row items-center',
                  {
                    'hidden lg:block': desktop,
                  },
                  className
                )}
              >
                {content}
              </motion.div>
            </TooltipPrimitive.Content>
          </TooltipPrimitive.Portal>
        )}
      </AnimatePresence>
    </TooltipPrimitive.Root>
  );
};
